// Cari detay karti — ticket sayfasi "Genel" sekmesi duzeni:
// sekme cubugu + sag ust Duzenle/Kaydet/Iptal + kartlar (FieldBox + outlined alanlar).
import { useEffect, useReducer, useRef, useState } from 'react';
import { TextBox, SelectBox } from 'devextreme-react';
import Form, { SimpleItem, GroupItem, Label } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import notify from 'devextreme/ui/notify';
import { Customer, emptyCustomer } from '../types';
import { saveCustomer } from '../api/customers';
import { lt } from '../i18n';
import './customer-card.css';

interface Props {
  customer: Customer | null;
  onSaved?: (saved: Customer) => void;
  onCancel?: () => void;
  /** Splitter ust panel (grid) durumu + daralt/genislet (ticket sayfasi gibi ok). */
  topCollapsed?: boolean;
  onToggleTop?: () => void;
}

const CARD_TYPES = [{ id: 1, name: 'Musteri (1)' }, { id: 2, name: 'Satici (2)' }, { id: 3, name: 'Her Ikisi (3)' }];
const PERS_COMPANY = [{ id: 0, name: 'Tuzel (0)' }, { id: 1, name: 'Sahis (1)' }];
const ACTIVE_OPTS = [{ id: true, name: 'Aktif' }, { id: false, name: 'Pasif' }];

type TabId = 'genel' | 'kimlik' | 'adres' | 'iletisim' | 'finans';
const TABS: Array<{ id: TabId; text: string; icon: string }> = [
  { id: 'genel', text: 'Genel', icon: 'info' },
  { id: 'kimlik', text: 'Kimlik', icon: 'card' },
  { id: 'adres', text: 'Adres', icon: 'home' },
  { id: 'iletisim', text: 'Iletisim', icon: 'tel' },
  { id: 'finans', text: 'Finans', icon: 'money' },
];

function FieldBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs mb-1 lc-label">{label}</label>
      {children}
    </div>
  );
}

export function CustomerDetailPane({ customer, onSaved, onCancel, topCollapsed, onToggleTop }: Props) {
  const [tab, setTab] = useState<TabId>('genel');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [, bump] = useReducer((x: number) => x + 1, 0);

  const modelRef = useRef<Customer>(customer ? { ...customer } : emptyCustomer());
  const isNew = !customer || !customer.id;

  useEffect(() => {
    modelRef.current = customer ? { ...customer } : emptyCustomer();
    setEditing(isNew);
    bump();
  }, [customer, isNew]);

  const m = modelRef.current;
  const ro = !editing;
  const set = (field: keyof Customer, value: any) => { (m as any)[field] = value; bump(); };

  const handleSave = async () => {
    if (!m.code?.trim() || !m.title?.trim()) { notify(`${lt('code')} / ${lt('title')} ?`, 'warning', 2500); return; }
    setSaving(true);
    const res = await saveCustomer(m);
    setSaving(false);
    if (res.ok) { notify(lt('savedOk'), 'success', 2500); setEditing(false); onSaved?.(res.data ?? m); }
    else notify(`${lt('saveError')}: ${res.message ?? ''}`, 'error', 4000);
  };

  const handleCancel = () => {
    if (isNew) { onCancel?.(); return; }
    modelRef.current = customer ? { ...customer } : emptyCustomer();
    setEditing(false); bump();
  };

  const cityTown = [m.town, m.city].filter(Boolean).join(' / ') || '—';
  const detailKey = `${customer?.id ?? 'new'}-${customer?.code ?? ''}-${editing ? 'e' : 'r'}`;

  return (
    <div className="lc-root">
      {/* Sekme cubugu — sade metin sekmeleri, aktifte kisa mavi alt cizgi */}
      <div className="lc-tabbar">
        {TABS.map((tb) => (
          <button key={tb.id} className={`lc-tab ${tab === tb.id ? 'active' : ''}`} onClick={() => setTab(tb.id)}>
            {tb.text}
          </button>
        ))}
        <span className="lc-tabbar-spacer" />
        {onToggleTop ? (
          <button type="button" className="splitter-collapse-btn" title="Ust paneli ac/kapat" onClick={onToggleTop}>
            <i className={topCollapsed ? 'dx-icon-chevrondown' : 'dx-icon-chevronup'} />
          </button>
        ) : (
          <button className="lc-collapse" title="Kucult/Genislet" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? 'v' : '^'}
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="p-3 sm:p-4 flex-1 min-h-0 overflow-auto flex flex-col">
          {/* Sag ust: Duzenle / Kaydet + Iptal */}
          <div className="flex shrink-0" style={{ justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
            {editing ? (
              <>
                <Button text={lt('save')} icon="save" type="default" stylingMode="contained"
                  disabled={saving || !m.title?.trim()} onClick={handleSave} />
                <Button text={lt('cancel')} icon="close" type="normal" stylingMode="contained" disabled={saving} onClick={handleCancel} />
              </>
            ) : (
              <Button text="Duzenle" icon="edit" type="default" stylingMode="contained" onClick={() => setEditing(true)} />
            )}
          </div>

          {tab === 'genel' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
              {/* Kart 1 — Cari Bilgileri (duzenlenebilir) */}
              <div className="rounded-lg p-5 shadow-sm flex flex-col lc-card">
                <h4 className="mb-4 pb-2 lc-card-title">Cari Bilgileri</h4>
                <div className="space-y-3">
                  <FieldBox label="Musteri Kodu">
                    <TextBox value={m.code} readOnly={ro} stylingMode="outlined" width="100%"
                      onValueChanged={(e) => set('code', e.value ?? '')} />
                  </FieldBox>
                  <FieldBox label="Musteri Adi">
                    <TextBox value={m.title} readOnly={ro} stylingMode="outlined" width="100%"
                      onValueChanged={(e) => set('title', e.value ?? '')} />
                  </FieldBox>
                  <FieldBox label="Musteri Grubu">
                    <TextBox value={m.groupCode ?? ''} readOnly={ro} stylingMode="outlined" width="100%"
                      onValueChanged={(e) => set('groupCode', e.value ?? '')} />
                  </FieldBox>
                  <FieldBox label="Kart Tipi">
                    <SelectBox dataSource={CARD_TYPES} value={m.cardType} valueExpr="id" displayExpr="name"
                      readOnly={ro} stylingMode="outlined" width="100%" onValueChanged={(e) => set('cardType', e.value)} />
                  </FieldBox>
                  <FieldBox label="Odeme Grubu">
                    <TextBox value={m.paymentGroup ?? ''} readOnly={ro} stylingMode="outlined" width="100%"
                      onValueChanged={(e) => set('paymentGroup', e.value ?? '')} />
                  </FieldBox>
                  <FieldBox label="Ticari Grup">
                    <TextBox value={m.tradingGroup ?? ''} readOnly={ro} stylingMode="outlined" width="100%"
                      onValueChanged={(e) => set('tradingGroup', e.value ?? '')} />
                  </FieldBox>
                  <FieldBox label="Durum">
                    <SelectBox dataSource={ACTIVE_OPTS} value={m.isActive} valueExpr="id" displayExpr="name"
                      readOnly={ro} stylingMode="outlined" width="100%" onValueChanged={(e) => set('isActive', e.value)} />
                  </FieldBox>
                </div>
              </div>

              {/* Kart 2 — ERP Musteri Bilgisi (salt-okunur) */}
              <div className="rounded-lg p-5 shadow-sm flex flex-col lc-card">
                <h4 className="mb-4 pb-2 lc-card-title">ERP Musteri Bilgisi</h4>
                <div className="space-y-3">
                  <FieldBox label="Kod"><TextBox value={m.code} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                  <FieldBox label="Unvan"><TextBox value={m.title} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                  <FieldBox label="Vergi No"><TextBox value={m.vatId || '—'} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                  <FieldBox label="Vergi Dairesi"><TextBox value={m.taxOffice || '—'} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                  <FieldBox label="Telefon"><TextBox value={m.phone1 || '—'} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                  <FieldBox label="E-posta"><TextBox value={m.email || '—'} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                </div>
              </div>

              {/* Kart 3 — ERP Sevkiyat Bilgisi (salt-okunur) */}
              <div className="rounded-lg p-5 shadow-sm flex flex-col lc-card">
                <h4 className="mb-4 pb-2 lc-card-title">ERP Sevkiyat Bilgisi</h4>
                <div className="space-y-3">
                  <FieldBox label="Adres"><TextBox value={m.address1 || '—'} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                  <FieldBox label="Ilce / Sehir"><TextBox value={cityTown} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                  <FieldBox label="Posta Kodu"><TextBox value={m.zipCode || '—'} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                  <FieldBox label="Ulke"><TextBox value={m.country || m.countryCode || '—'} readOnly stylingMode="outlined" width="100%" /></FieldBox>
                </div>
              </div>
            </div>
          ) : (
            <div className="lc-detail-form">
              <Form key={`${detailKey}-${tab}`} formData={m} labelLocation="top" showColonAfterLabel colCount={3} readOnly={ro}>
                {tab === 'kimlik' && (
                  <GroupItem colCount={3}>
                    <SimpleItem dataField="code"><Label text="Kod" /></SimpleItem>
                    <SimpleItem dataField="title" colSpan={2}><Label text="Unvan" /></SimpleItem>
                    <SimpleItem dataField="title2" colSpan={2}><Label text="Unvan 2" /></SimpleItem>
                    <SimpleItem dataField="cardType" editorType="dxSelectBox"
                      editorOptions={{ items: CARD_TYPES, valueExpr: 'id', displayExpr: 'name' }}><Label text="Kart Tipi" /></SimpleItem>
                    <SimpleItem dataField="name"><Label text="Ad" /></SimpleItem>
                    <SimpleItem dataField="surname"><Label text="Soyad" /></SimpleItem>
                    <SimpleItem dataField="persCompany" editorType="dxSelectBox"
                      editorOptions={{ items: PERS_COMPANY, valueExpr: 'id', displayExpr: 'name' }}><Label text="Kisi/Firma" /></SimpleItem>
                    <SimpleItem dataField="vatId"><Label text="Vergi No" /></SimpleItem>
                    <SimpleItem dataField="tcKimlikNo"><Label text="TC Kimlik No" /></SimpleItem>
                    <SimpleItem dataField="taxOffice"><Label text="Vergi Dairesi" /></SimpleItem>
                    <SimpleItem dataField="isActive" editorType="dxCheckBox"><Label text="Aktif" /></SimpleItem>
                  </GroupItem>
                )}
                {tab === 'adres' && (
                  <GroupItem colCount={3}>
                    <SimpleItem dataField="address1" colSpan={3}><Label text="Adres 1" /></SimpleItem>
                    <SimpleItem dataField="address2" colSpan={3}><Label text="Adres 2" /></SimpleItem>
                    <SimpleItem dataField="district"><Label text="Semt" /></SimpleItem>
                    <SimpleItem dataField="town"><Label text="Ilce" /></SimpleItem>
                    <SimpleItem dataField="city"><Label text="Sehir" /></SimpleItem>
                    <SimpleItem dataField="countryCode"><Label text="Ulke Kodu" /></SimpleItem>
                    <SimpleItem dataField="country"><Label text="Ulke" /></SimpleItem>
                    <SimpleItem dataField="zipCode"><Label text="Posta Kodu" /></SimpleItem>
                  </GroupItem>
                )}
                {tab === 'iletisim' && (
                  <GroupItem colCount={3}>
                    <SimpleItem dataField="phone1"><Label text="Telefon 1" /></SimpleItem>
                    <SimpleItem dataField="phone2"><Label text="Telefon 2" /></SimpleItem>
                    <SimpleItem dataField="fax"><Label text="Faks" /></SimpleItem>
                    <SimpleItem dataField="email"><Label text="E-posta" /></SimpleItem>
                    <SimpleItem dataField="email2"><Label text="E-posta 2" /></SimpleItem>
                    <SimpleItem dataField="webUrl"><Label text="Web" /></SimpleItem>
                    <SimpleItem dataField="contact"><Label text="Yetkili 1" /></SimpleItem>
                    <SimpleItem dataField="contact2"><Label text="Yetkili 2" /></SimpleItem>
                    <SimpleItem dataField="contact3"><Label text="Yetkili 3" /></SimpleItem>
                  </GroupItem>
                )}
                {tab === 'finans' && (
                  <GroupItem colCount={3}>
                    <SimpleItem dataField="creditLimit" editorType="dxNumberBox"><Label text="Kredi Limiti" /></SimpleItem>
                    <SimpleItem dataField="riskType1" editorType="dxNumberBox"><Label text="Risk 1" /></SimpleItem>
                    <SimpleItem dataField="riskType2" editorType="dxNumberBox"><Label text="Risk 2" /></SimpleItem>
                    <SimpleItem dataField="specode1"><Label text="Ozel Kod 1" /></SimpleItem>
                    <SimpleItem dataField="specode2"><Label text="Ozel Kod 2" /></SimpleItem>
                    <SimpleItem dataField="specode3"><Label text="Ozel Kod 3" /></SimpleItem>
                    <SimpleItem dataField="orgLogoId"><Label text="Org Logo Id" /></SimpleItem>
                    <SimpleItem dataField="factoryDivNr" editorType="dxNumberBox"><Label text="Isyeri No" /></SimpleItem>
                  </GroupItem>
                )}
              </Form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerDetailPane;
